import cookie from 'js-cookie'
import { Redirect } from 'react-router-dom'

import { PAGE } from '@/app/definitions'
import { TRoute } from '@/app/service/render/wrapper'

import AccountRoute from './account/account'
import BillingRoute from './account/billing'
import OrganizationsRoute from './account/organization'
import CampaignsRoute from './campaigns'
import CampaignsCreateRoute from './campaigns/create'
import CampaignsDashboardRoute, { resolveDashboardTab } from './campaigns/dashboard'
import CampaignsItemRoute from './campaigns/item'
import CampaignsManageRoute from './campaigns/manage'
import CampaignsActionRulesRoute from './campaigns/rules'
import CATISubs from './cati/subs'
import CATISurvey from './cati/survey'
import CheckInvitationRoute from './check-invitation'
import ContactsRoute from './contacts'
import ContactsAddItemRoute from './contacts/add'
import ContactsEditItemRoute from './contacts/edit'
import ContentRoute from './content'
import ConversationsRoute from './conversations'
import DashboardRoute from './dashboard'
import LoginRoute from './login'
import LogsRoute from './logs'
import NoOrgRoute from './no-org'
import NotFoundRoute from './not-found'
import PhoneNumbersRoute from './phonenumbers'
import PhoneNumbersItemRoute from './phonenumbers/item'
import PlansRoute from './plans'
import ReportsRoute from './reports'
import ReportFileRoute from './reports/report-file'
import ResetPasswordRoute from './reset-password'
import SignupRoute from './signup'
import TermsRoute from './terms-of-service'
import VerifyOTPRoute from './verify-otp'
import VerifyRecoveryCodeRoute from './verify-recovery-code'

const routes: TRoute[] = [
  {
    name: PAGE.HOME,
    path: '/',
    exact: true,
    component: () => {
      const redirectURL = cookie.get('redirectURL')
      cookie.remove('redirectURL')
      return redirectURL ? <Redirect to={redirectURL} /> : <Redirect to="/dashboard" />
    },
  },
  {
    name: PAGE.CONTACTS.INDEX,
    path: '/contacts',
    exact: true,
    component: ContactsRoute,
  },
  {
    name: PAGE.CONTACTS.INDEX,
    path: '/contacts/page/:page',
    exact: true,
    component: ({ match }) => <Redirect to={`/contacts/?page=${match.params.page}`} />,
  },
  {
    name: PAGE.CONTACTS.ADD,
    path: '/contacts/add',
    exact: true,
    component: ContactsAddItemRoute,
  },
  {
    name: PAGE.CONTACTS.EDIT,
    path: '/contacts/edit/:contactsId',
    exact: true,
    component: ContactsEditItemRoute,
  },
  {
    name: PAGE.CONTACTS.UPLOAD,
    path: '/contacts/upload',
    exact: true,
    component: ContactsRoute,
  },
  {
    name: PAGE.CONTACTS.SEARCH,
    path: '/contacts/search/:searchTerm',
    exact: true,
    component: ({ match }) => <Redirect to={`/contacts/?search=${match.params.searchTerm}`} />,
  },
  {
    name: PAGE.CONTACTS.SEARCH,
    path: '/contacts/search/:searchTerm/page/:page',
    exact: true,
    component: ({ match }) => (
      <Redirect to={`/contacts/?search=${match.params.searchTerm}&page=${match.params.page}`} />
    ),
  },
  {
    name: PAGE.CONTACTS.FILTER,
    path: '/contacts/filters/:filterType/:filterId',
    exact: true,
    component: ({ match }) => (
      <Redirect to={`/contacts/?filterType=${match.params.filterType}&filterId=${match.params.filterId}`} />
    ),
  },
  {
    name: PAGE.CONTACTS.FILTER,
    path: '/contacts/filters/:filterType/:filterId/page/:page',
    exact: true,
    component: ({ match }) => (
      <Redirect
        to={`/contacts/?filterType=${match.params.filterType}&filterId=${match.params.filterId}&page=${match.params.page}`}
      />
    ),
  },
  {
    name: PAGE.LOGS.INDEX,
    path: '/logs',
    exact: true,
    component: LogsRoute,
  },
  {
    name: PAGE.MESSAGES.INDEX,
    path: '/messages',
    component: () => <Redirect to={'/logs'} />,
  },
  {
    name: PAGE.CONVERSATIONS.INDEX,
    path: '/conversations',
    exact: true,
    component: ConversationsRoute,
  },
  {
    name: PAGE.PHONENUMBERS.INDEX,
    path: '/phonenumbers',
    exact: true,
    component: PhoneNumbersRoute,
  },
  {
    name: PAGE.PHONENUMBERS.ITEM,
    path: '/phonenumbers/:phoneNumberId',
    exact: true,
    component: ({ match }) => <Redirect to={`/phonenumbers/${match.params.phoneNumberId}/sms`} />,
  },
  {
    name: PAGE.PHONENUMBERS.ITEM,
    path: '/phonenumbers/:phoneNumberId/:tabName',
    exact: true,
    component: PhoneNumbersItemRoute,
  },
  {
    name: PAGE.DASHBOARD.INDEX,
    path: '/dashboard',
    exact: true,
    component: DashboardRoute,
  },
  {
    name: PAGE.PLANS.INDEX,
    path: '/plans',
    exact: true,
    component: PlansRoute,
  },
  {
    name: PAGE.CAMPAIGNS.INDEX,
    path: '/campaigns',
    exact: true,
    component: CampaignsRoute,
  },
  {
    name: PAGE.CAMPAIGNS.ADD,
    path: '/campaigns/create',
    exact: true,
    component: CampaignsCreateRoute,
  },
  {
    name: PAGE.CAMPAIGNS.ADD,
    path: '/campaigns/create/:campaignType',
    exact: true,
    component: CampaignsItemRoute,
  },
  {
    name: PAGE.CAMPAIGNS.EDIT,
    path: '/campaigns/edit/:campaignsId',
    exact: true,
    component: CampaignsItemRoute,
  },
  {
    name: PAGE.CAMPAIGNS.EDIT,
    path: '/campaigns/edit/:campaignsId/:step',
    exact: true,
    component: CampaignsItemRoute,
  },
  {
    name: PAGE.CAMPAIGNS.MANAGE,
    path: '/campaigns/manage/:campaignsId',
    exact: true,
    component: CampaignsManageRoute,
  },
  {
    name: PAGE.CAMPAIGNS.DASHBOARD,
    path: '/campaigns/summary/:campaignsId([0-9]+)/:step?',
    exact: true,
    component: ({ match, location }) => {
      const tab = resolveDashboardTab(match.params.step)
      const pathname = `/campaigns/${match.params.campaignsId}${tab === 'overview' ? '' : `/${tab}`}`
      return <Redirect to={{ ...location, pathname }} />
    },
  },
  {
    name: PAGE.CAMPAIGNS.RULES,
    path: '/campaigns/action-rules',
    exact: true,
    component: CampaignsActionRulesRoute,
  },
  {
    name: PAGE.CAMPAIGNS.DASHBOARD,
    path: '/campaigns/:campId([0-9]+)/:tabName',
    exact: true,
    component: CampaignsDashboardRoute,
  },
  {
    name: PAGE.CAMPAIGNS.DASHBOARD,
    path: '/campaigns/:campId([0-9]+)',
    exact: true,
    component: CampaignsDashboardRoute,
  },
  {
    name: PAGE.CONTENT.INDEX,
    path: '/content',
    exact: true,
    component: ContentRoute,
  },
  {
    name: PAGE.CONTENT.INDEX,
    path: '/content/:contentType',
    exact: true,
    component: ContentRoute,
  },
  {
    name: PAGE.ACCOUNT.LOGIN,
    path: '/login',
    exact: true,
    component: LoginRoute,
  },
  {
    name: PAGE.ACCOUNT.SIGNUP,
    path: '/signup',
    exact: true,
    component: SignupRoute,
  },
  {
    name: PAGE.ACCOUNT.REQUEST_RESET_PASSWORD,
    path: '/request-reset-password',
    exact: true,
    component: ResetPasswordRoute,
  },
  {
    name: PAGE.ACCOUNT.VERIFY_OTP,
    path: '/2fa',
    exact: true,
    component: VerifyOTPRoute,
  },
  {
    name: PAGE.ACCOUNT.VERIFY_RECOVERY_CODE,
    path: '/recovery-code',
    exact: true,
    component: VerifyRecoveryCodeRoute,
  },
  {
    name: PAGE.ACCOUNT.RESET_PASSWORD,
    path: '/reset-password',
    exact: true,
    component: ResetPasswordRoute,
  },
  {
    name: PAGE.INVITATION,
    path: '/invite',
    component: CheckInvitationRoute,
  },
  // Redirect old account routes to new routes
  {
    name: PAGE.ACCOUNT.BILLING.INDEX,
    path: '/account/billing',
    component: () => <Redirect to="/billing/overview" />,
  },
  {
    name: PAGE.ACCOUNT.ORGANIZATION.INDEX,
    path: '/account/organization/subscription',
    component: () => <Redirect to="/organization/subscription" />,
  },
  {
    name: PAGE.ACCOUNT.ORGANIZATION.INDEX,
    path: '/account/organization/privacy',
    component: () => <Redirect to="/organization/privacy" />,
  },
  {
    name: PAGE.ACCOUNT.ORGANIZATION.INDEX,
    path: '/account/organization/users',
    component: () => <Redirect to="/organization/users" />,
  },
  {
    name: PAGE.ACCOUNT.ORGANIZATION.INDEX,
    path: '/account/organization/audit',
    component: () => <Redirect to="/organization/audit" />,
  },
  {
    name: PAGE.ACCOUNT.ORGANIZATION.INDEX,
    path: '/account/organization/cx-uploads',
    component: () => <Redirect to="/organization/cx-uploads" />,
  },
  {
    name: PAGE.ACCOUNT.ORGANIZATION.INDEX,
    path: '/account/organization/custom-fields',
    component: () => <Redirect to="/organization/custom-fields" />,
  },
  {
    name: PAGE.ACCOUNT.ORGANIZATION.INDEX,
    path: '/account/organization',
    component: () => <Redirect to="/organization/info" />,
  },
  {
    name: PAGE.ACCOUNT.PROFILE.INDEX,
    path: '/account/profile/info',
    component: () => <Redirect to="/account/profile" />,
  },
  {
    name: PAGE.ACCOUNT.RESET_PASSWORD,
    path: '/account/profile/reset-password',
    component: () => <Redirect to="/account/reset-password" />,
  },
  {
    name: PAGE.ACCOUNT.TWO_FA,
    path: '/account/profile/two-fa',
    component: () => <Redirect to="/account/2fa" />,
  },
  {
    name: PAGE.ACCOUNT.API_TOKENS,
    path: '/account/profile/api-tokens',
    component: () => <Redirect to="/account/api-tokens" />,
  },
  // End redirect old account routes to new routes
  {
    name: PAGE.ACCOUNT.PROFILE.INDEX,
    path: '/account',
    exact: true,
    component: () => <Redirect to="/account/profile" />,
  },
  {
    name: PAGE.ACCOUNT.PROFILE.INDEX,
    path: '/account/:tabName',
    exact: true,
    component: AccountRoute,
  },
  {
    name: PAGE.ACCOUNT.ORGANIZATION.INDEX,
    path: '/organization',
    exact: true,
    component: () => <Redirect to="/organization/info" />,
  },
  {
    name: PAGE.ACCOUNT.ORGANIZATION.INDEX,
    path: '/organization/:tabName',
    exact: true,
    component: OrganizationsRoute,
  },
  {
    name: PAGE.ACCOUNT.BILLING.INDEX,
    path: '/billing/:tabName',
    exact: true,
    component: BillingRoute,
  },
  {
    name: PAGE.ACCOUNT.BILLING.INDEX,
    path: '/billing/pay',
    component: () => <Redirect to="/billing/overview" />,
  },
  {
    name: PAGE.ACCOUNT.BILLING.INDEX,
    path: '/billing',
    exact: true,
    component: () => <Redirect to="/billing/overview" />,
  },
  {
    name: PAGE.REPORTS.INDEX,
    path: '/reports',
    exact: true,
    component: () => <Redirect to="/reports/campreport" />,
  },
  {
    name: PAGE.REPORTS.INDEX,
    path: '/reports/:reportType',
    exact: true,
    component: ReportsRoute,
  },
  {
    name: PAGE.REPORTS.INDEX,
    path: '/reports/:reportType/create',
    exact: true,
    component: ReportsRoute,
  },
  {
    name: PAGE.REPORTS.FILE,
    path: '/reports/file/:exportId',
    component: ReportFileRoute,
  },
  {
    name: PAGE.TERMS,
    path: '/terms-of-service',
    exact: true,
    component: TermsRoute,
  },
  {
    name: PAGE.CATI.SURVEY,
    path: '/cati/:itemId/sub/:runId',
    exact: true,
    component: CATISurvey,
  },
  {
    name: PAGE.CATI.SUBS,
    path: '/cati/contacts',
    exact: true,
    component: CATISubs,
  },
  {
    name: PAGE.NOORG,
    path: '/no-org',
    exact: true,
    component: ({ match }) => {
      const redirectURL = cookie.get('redirectURL')
      cookie.remove('redirectURL')
      return redirectURL ? <Redirect to={redirectURL} /> : <NoOrgRoute match={match} />
    },
  },
  {
    name: PAGE.NOTFOUND,
    path: '**',
    component: NotFoundRoute,
  },
]

export default routes
